// Appearance: theme, chat background (preset, colour or your own photo; for
// all chats or one) and message bubble colours. Saved on this device, like
// WhatsApp's wallpapers; applied as CSS custom properties with a light and a
// dark variant of every preset.
import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import * as S from "../state";
import { Icon } from "./Icon";
import { toast } from "./Main";
import { Drawer } from "./Panels";
import { applyTheme } from "./Shortcuts";
import { playSent, setSounds, soundsOn } from "../sounds";

type Pair = [light: string, dark: string];
interface Wall {
  id: string;
  label: string;
  color: Pair;
  image?: Pair; // CSS background-image
  size?: string;
}

const dots = (c: string) => `radial-gradient(${c} 1px, transparent 1px)`;
const grid = (c: string) => `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`;

export const WALLS: Wall[] = [
  { id: "plain", label: "Plain", color: ["#f7f7f5", "#0b1014"] },
  { id: "classic", label: "Classic", color: ["#efeae2", "#0b141a"], image: [dots("rgba(102,119,129,.14)"), dots("rgba(134,150,160,.10)")], size: "22px 22px" },
  { id: "grid", label: "Grid", color: ["#f6f7f9", "#0d1317"], image: [grid("rgba(17,27,33,.05)"), grid("rgba(255,255,255,.035)")], size: "24px 24px" },
  { id: "mint", label: "Mint", color: ["#e6f4ec", "#0d1a15"] },
  { id: "sky", label: "Sky", color: ["#e5f0fa", "#0c1520"] },
  { id: "peach", label: "Peach", color: ["#fbebe1", "#1d1410"] },
  { id: "lavender", label: "Lavender", color: ["#eee9f8", "#15121f"] },
  { id: "sand", label: "Sand", color: ["#f3eee3", "#17140e"] },
  { id: "sunset", label: "Sunset", color: ["#fde6d8", "#1d1218"], image: ["linear-gradient(160deg, #fde2cf, #f7d6e6 55%, #e5dcf7)", "linear-gradient(160deg, #2a1717, #24142a 55%, #161a2c)"] },
  { id: "ocean", label: "Ocean", color: ["#dff0f3", "#0b1a1f"], image: ["linear-gradient(160deg, #e2f4f1, #dbe9f8)", "linear-gradient(160deg, #0d2026, #0f1830)"] },
  { id: "forest", label: "Forest", color: ["#e3efe2", "#0e1a12"], image: ["linear-gradient(160deg, #e9f3e3, #d9eadf)", "linear-gradient(160deg, #132417, #0d1b16)"] },
  { id: "night", label: "Night", color: ["#1f2933", "#05080b"] },
];

interface Bubble {
  id: string;
  label: string;
  out: Pair;
}
export const BUBBLES: Bubble[] = [
  { id: "green", label: "Green", out: ["#e3f6dd", "#005c4b"] },
  { id: "teal", label: "Teal", out: ["#d5f3f1", "#0b5559"] },
  { id: "blue", label: "Blue", out: ["#dcebff", "#1d4f8f"] },
  { id: "purple", label: "Purple", out: ["#ebe2fb", "#4b3480"] },
  { id: "pink", label: "Pink", out: ["#fde1ec", "#7c2850"] },
  { id: "orange", label: "Orange", out: ["#ffe9d6", "#7a4210"] },
  { id: "yellow", label: "Yellow", out: ["#fff5c8", "#6b5a12"] },
  { id: "gray", label: "Gray", out: ["#eceef0", "#2c363d"] },
];
const IN_BUBBLES: Bubble[] = [
  { id: "white", label: "White", out: ["#ffffff", "#1d262c"] },
  { id: "gray", label: "Gray", out: ["#eff1f3", "#252f36"] },
  { id: "tinted", label: "Tinted", out: ["#f2f7f6", "#1c2a2c"] },
];

interface Background {
  wall: string; // preset id, "color" or "photo"
  color?: string; // custom colour
  photo?: string; // data URL
  dim?: boolean; // darken the photo in dark mode
}
interface Prefs {
  bg: Background;
  chats: Record<string, Background>; // per-chat backgrounds
  out: string; // bubble preset id or "#rrggbb"
  in: string;
}

const KEY = "xmm.appearance";
const DEFAULTS: Prefs = { bg: { wall: "plain" }, chats: {}, out: "green", in: "white" };

function load(): Prefs {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<Prefs>) };
  } catch {
    return DEFAULTS;
  }
}
export const appearance = signal<Prefs>(load());

function save(p: Prefs) {
  appearance.value = p;
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    toast("Couldn't save: this photo is too big for this browser's storage");
  }
  applyAppearance();
}

// ---- colours -------------------------------------------------------------------------

function luminance(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
/** Readable text (and its secondary shade) on a bubble colour. */
const textOn = (hex: string): [string, string] => (luminance(hex) > 0.35 ? ["#111b21", "rgba(17,27,33,.55)"] : ["#f3f6f7", "rgba(243,246,247,.65)"]);
/** A darker version of a custom colour for dark mode. */
function shade(hex: string, f: number) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v * f));
  return "#" + ch.map((v) => v.toString(16).padStart(2, "0")).join("");
}
const isHex = (s: string) => /^#[0-9a-f]{6}$/i.test(s);

function bubblePair(v: string, list: Bubble[]): Pair {
  if (isHex(v)) return [v, luminance(v) > 0.35 ? shade(v, 0.42) : v];
  return (list.find((b) => b.id === v) ?? list[0]).out;
}

function wallVars(b: Background, dark: boolean): string {
  const i = dark ? 1 : 0;
  if (b.wall === "photo" && b.photo) {
    const dim = dark && b.dim !== false ? "linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45)), " : "";
    return `--chat-bg:${dark ? "#0b1014" : "#f7f7f5"};--chat-pattern:${dim}url("${b.photo}");--chat-pattern-size:cover;`;
  }
  if (b.wall === "color" && b.color && isHex(b.color)) {
    const c = dark && luminance(b.color) > 0.2 ? shade(b.color, 0.18) : b.color;
    return `--chat-bg:${c};--chat-pattern:none;`;
  }
  const w = WALLS.find((x) => x.id === b.wall) ?? WALLS[0];
  return `--chat-bg:${w.color[i]};--chat-pattern:${w.image?.[i] ?? "none"};--chat-pattern-size:${w.size ?? "cover"};`;
}

function bubbleVars(p: Prefs, dark: boolean): string {
  const i = dark ? 1 : 0;
  const out = bubblePair(p.out, BUBBLES)[i];
  const inn = bubblePair(p.in, IN_BUBBLES)[i];
  const [ot, ot2] = textOn(out);
  const [it, it2] = textOn(inn);
  return `--bubble-out:${out};--bubble-out-text:${ot};--bubble-out-meta:${ot2};--bubble-in:${inn};--bubble-in-text:${it};--bubble-in-meta:${it2};`;
}

/** A class for a chat with its own background ("" = the default one). */
export function wallClass(jid: string) {
  if (!appearance.value.chats[jid]) return "";
  let h = 0;
  for (const c of jid) h = (h * 31 + c.charCodeAt(0)) | 0;
  return "wall-" + (h >>> 0).toString(36);
}

/** (Re)write the style sheet with the current choices: light rules, then
 * the dark ones (for the system's dark mode and for the dark theme). */
export function applyAppearance() {
  const p = appearance.value;
  const rules = (dark: boolean) => {
    let css = `:root{${wallVars(p.bg, dark)}${bubbleVars(p, dark)}}`;
    for (const [jid, b] of Object.entries(p.chats)) css += `.${wallClass(jid)} .messages{${wallVars(b, dark)}}`;
    return css;
  };
  let el = document.getElementById("xmm-appearance") as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = "xmm-appearance";
    document.head.appendChild(el);
  }
  el.textContent = [
    rules(false),
    `@media (prefers-color-scheme: dark){${scoped(rules(true), ':root:not([data-theme="light"])')}}`,
    scoped(rules(true), ':root[data-theme="dark"]'),
  ].join("\n");
}

/** Prefix every rule of `css` (":root{…}.wall-x .messages{…}") with a root selector. */
function scoped(css: string, root: string) {
  return css.replace(/(^|\})([^{}]+)\{/g, (_m, end: string, sel: string) => `${end}${sel.trim() === ":root" ? root : `${root} ${sel.trim()}`}{`);
}

// ---- photo upload: scaled down so it fits in this device's storage ---------------------

async function photoToDataURL(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const s = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * s);
  c.height = Math.round(bmp.height * s);
  c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close();
  return c.toDataURL("image/jpeg", 0.78);
}

// ---- the panel ----------------------------------------------------------------------

export function AppearancePanel() {
  const p = appearance.value;
  const chat = S.activeChat.value;
  const [scope, setScope] = useState<"all" | "chat">(chat && p.chats[chat] ? "chat" : "all");
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("xmm.theme") ?? "system";
    } catch {
      return "system";
    }
  });
  const target: Background = scope === "chat" && chat ? (p.chats[chat] ?? p.bg) : p.bg;
  const setBg = (b: Background) => save(scope === "chat" && chat ? { ...p, chats: { ...p.chats, [chat]: b } } : { ...p, bg: b });
  const resetChat = () => {
    if (!chat) return;
    const { [chat]: _, ...rest } = p.chats;
    save({ ...p, chats: rest });
    setScope("all");
  };
  const preview = (b: Background) => {
    const w = WALLS.find((x) => x.id === b.wall);
    if (b.wall === "photo" && b.photo) return { backgroundImage: `url("${b.photo}")`, backgroundSize: "cover" };
    if (b.wall === "color") return { background: b.color };
    return { backgroundColor: "var(--swatch-bg)", backgroundImage: w?.image ? "var(--swatch-img)" : undefined, "--swatch-bg": w?.color[0], "--swatch-img": w?.image?.[0], backgroundSize: w?.size === "cover" || !w?.size ? "cover" : "10px 10px" };
  };

  return (
    <Drawer title="Appearance">
      <section class="appear-section">
        <h3>Theme</h3>
        <div class="seg" role="radiogroup" aria-label="Theme">
          {(["system", "light", "dark"] as const).map((t) => (
            <button key={t} type="button" role="radio" aria-checked={theme === t} class={"seg-btn" + (theme === t ? " on" : "")} onClick={() => (applyTheme(t), setTheme(t))}>
              {t === "system" ? "System" : t === "light" ? "Light" : "Dark"}
            </button>
          ))}
        </div>
      </section>

      <section class="appear-section">
        <h3>Chat background</h3>
        {chat && (
          <div class="seg" role="radiogroup" aria-label="Apply to">
            <button type="button" role="radio" aria-checked={scope === "all"} class={"seg-btn" + (scope === "all" ? " on" : "")} onClick={() => setScope("all")}>
              All chats
            </button>
            <button type="button" role="radio" aria-checked={scope === "chat"} class={"seg-btn" + (scope === "chat" ? " on" : "")} onClick={() => setScope("chat")}>
              Only {S.displayName(chat)}
            </button>
          </div>
        )}
        <div class="swatches walls">
          {WALLS.map((w) => (
            <button
              key={w.id}
              class={"swatch" + (target.wall === w.id ? " on" : "")}
              style={preview({ wall: w.id })}
              onClick={() => setBg({ wall: w.id })}
              aria-label={`Background ${w.label}`}
              aria-pressed={target.wall === w.id}
              title={w.label}
            >
              {target.wall === w.id && <Icon name="check" size={16} />}
            </button>
          ))}
          <label class={"swatch custom" + (target.wall === "color" ? " on" : "")} title="Custom colour" style={target.wall === "color" ? preview(target) : undefined}>
            <input type="color" value={target.color ?? "#d9e7e1"} onInput={(e) => setBg({ wall: "color", color: e.currentTarget.value })} aria-label="Custom background colour" />
            <Icon name="edit" size={16} />
          </label>
          <label class={"swatch custom" + (target.wall === "photo" ? " on" : "")} title="Your photo" style={target.wall === "photo" ? preview(target) : undefined}>
            <input
              type="file"
              accept="image/*"
              hidden
              aria-label="Background photo"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                e.currentTarget.value = "";
                if (f) void photoToDataURL(f).then((photo) => setBg({ wall: "photo", photo, dim: true }), () => toast("Couldn't read that photo"));
              }}
            />
            <Icon name="image" size={16} />
          </label>
        </div>
        {target.wall === "photo" && (
          <label class="toggle">
            <span>
              <strong>Dim the photo in dark mode</strong>
            </span>
            <input type="checkbox" checked={target.dim !== false} onChange={(e) => setBg({ ...target, dim: e.currentTarget.checked })} aria-label="Dim in dark mode" />
          </label>
        )}
        {scope === "chat" && chat && p.chats[chat] && (
          <button class="link" onClick={resetChat}>
            Use the default background in this chat
          </button>
        )}
      </section>

      <section class="appear-section">
        <h3>Your messages</h3>
        <BubblePicker value={p.out} list={BUBBLES} label="Your bubble" onPick={(v) => save({ ...p, out: v })} />
        <h3>Their messages</h3>
        <BubblePicker value={p.in} list={IN_BUBBLES} label="Their bubble" onPick={(v) => save({ ...p, in: v })} />
      </section>

      <section class="appear-section">
        <h3>Sounds</h3>
        <SoundsToggle />
      </section>

      <section class="appear-section">
        <h3>Preview</h3>
        <div class={"appear-preview " + (scope === "chat" && chat ? wallClass(chat) : "")}>
          <div class="messages">
            <div class="row in">
              <div class="bubble">
                <span class="text">Hi! How does this look?</span>
                <span class="meta">10:42</span>
              </div>
            </div>
            <div class="row out">
              <div class="bubble">
                <span class="text">Pretty good 👌</span>
                <span class="meta">10:43</span>
              </div>
            </div>
          </div>
        </div>
        <button class="link" onClick={() => save({ ...DEFAULTS, chats: {} })}>
          Reset everything
        </button>
      </section>
    </Drawer>
  );
}

function BubblePicker({ value, list, label, onPick }: { value: string; list: Bubble[]; label: string; onPick: (v: string) => void }) {
  return (
    <div class="swatches bubbles">
      {list.map((b) => (
        <button key={b.id} class={"swatch round" + (value === b.id ? " on" : "")} style={{ background: b.out[0] }} onClick={() => onPick(b.id)} aria-label={`${label} ${b.label}`} aria-pressed={value === b.id} title={b.label}>
          {value === b.id && <Icon name="check" size={14} />}
        </button>
      ))}
      <label class={"swatch round custom" + (isHex(value) ? " on" : "")} style={isHex(value) ? { background: value } : undefined} title="Custom colour">
        <input type="color" value={isHex(value) ? value : "#cfe8ff"} onInput={(e) => onPick(e.currentTarget.value)} aria-label={`${label} custom colour`} />
        <Icon name="edit" size={14} />
      </label>
    </div>
  );
}

function SoundsToggle() {
  const [on, setOn] = useState(soundsOn());
  return (
    <label class="toggle">
      <span>
        <strong>Conversation tones</strong>
        <small class="muted">A soft sound when you send a message (this device).</small>
      </span>
      <input type="checkbox" checked={on} onChange={(e) => (setSounds(e.currentTarget.checked), setOn(e.currentTarget.checked), e.currentTarget.checked && playSent())} aria-label="Conversation tones" />
    </label>
  );
}
